import axios from 'axios';
import { Endpoint } from 'types/url';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  AddServicePrincipalToDevOpsResponse,
  AvailableRepositoriesResponse,
  ConnectionCountResponse,
  DisconnectResponse,
  IntegrationOptionsResponse,
  MasterAppConsentStartRequest,
  MasterAppConsentStartResponse,
  MasterAppConsentStatusResponse,
  PATConnectOrgRequest,
  PATConnectOrgResponse,
  PATConnectResponse,
  StartOAuthResponse,
  SyncRepositoriesRequest,
  SyncRepositoriesResponse,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosAzureDevOpsInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosAzureDevOpsInstance);

export class AzureDevOpsClient extends BaseHttpClient {
  constructor() {
    super(axiosAzureDevOpsInstance);
  }

  // Singleton instance for access without instantiation
  static override instance = new AzureDevOpsClient();

  static async startOAuth() {
    return await AzureDevOpsClient.post<StartOAuthResponse>(
      'azure/oauth/start' as Endpoint,
      {},
    );
  }

  static async getAvailableRepositories(
    forceRefresh = false,
    connectionMethod?: string | null,
    azureTenantId?: string,
  ) {
    const searchParams = new URLSearchParams();

    if (forceRefresh) {
      searchParams.append('force_refresh', 'true');
    }

    if (connectionMethod) {
      searchParams.append('connection_method', connectionMethod);
    }

    if (azureTenantId) {
      searchParams.append('azure_tenant_id', azureTenantId);
    }

    return await AzureDevOpsClient.get<AvailableRepositoriesResponse>(
      'azure/available-repositories' as Endpoint,
      { params: searchParams },
    );
  }

  static async syncRepositories(request: SyncRepositoriesRequest) {
    return await AzureDevOpsClient.post<SyncRepositoriesResponse>(
      'azure/sync-repositories' as Endpoint,
      request,
    );
  }

  static async disconnect() {
    return await AzureDevOpsClient.delete<DisconnectResponse>(
      'azure/oauth/disconnect' as Endpoint,
    );
  }

  // Azure Master App Methods
  static async startMasterAppConsent(params: MasterAppConsentStartRequest) {
    return await AzureDevOpsClient.post<MasterAppConsentStartResponse>(
      'azure/master-app/consent/start' as Endpoint,
      params,
    );
  }

  static async getMasterAppConsentStatus(azureTenantId?: string) {
    const params = new URLSearchParams();

    if (azureTenantId) {
      params.append('azure_tenant_id', azureTenantId);
    }

    return await AzureDevOpsClient.get<MasterAppConsentStatusResponse>(
      'azure/master-app/consent/status' as Endpoint,
      params.size > 0 ? { params } : undefined,
    );
  }

  static async getIntegrationOptions() {
    return await AzureDevOpsClient.get<IntegrationOptionsResponse>(
      'azure/integration-options' as Endpoint,
    );
  }

  static async addServicePrincipalToDevOps(
    organizationName?: string,
    azureTenantId?: string,
  ) {
    return await AzureDevOpsClient.post<AddServicePrincipalToDevOpsResponse>(
      'azure/service-principal/add-to-devops' as Endpoint,
      { organizationName, azureTenantId },
    );
  }

  static async checkAzureInstallationsStatus() {
    return await AzureDevOpsClient.get<ConnectionCountResponse>(
      'azure/connection/count' as Endpoint,
    );
  }

  // PAT Methods
  static async connectWithPAT(pat: string) {
    return await AzureDevOpsClient.post<PATConnectResponse>(
      'azure/pat/connect' as Endpoint,
      { pat } satisfies { pat: string },
    );
  }

  static async connectWithPATAndOrg(pat: string, orgUrl: string) {
    return await AzureDevOpsClient.post<PATConnectOrgResponse>(
      'azure/pat/connect-org' as Endpoint,
      { pat, orgUrl } satisfies PATConnectOrgRequest,
    );
  }
}
